import React, { useState } from 'react';
import {
  View,
  TextInput,
  TouchableOpacity,
  Text,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { RootStackParamList } from '../../types/navigation';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../styles/ThemeProvider';
import { useCommonStyles } from '../../styles/useCommonStyles';

type Props = NativeStackScreenProps<RootStackParamList, 'EmailSignUp'>;

export default function EmailSignUpScreen({ navigation }: Props) {
  const theme = useTheme();
  const commonStyles = useCommonStyles();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notification, setNotification] = useState('');
  const { signUp } = useAuth();

  const handleSignUp = async () => {
    setError('');
    setNotification('');

    if (!email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await signUp(email, password);
      setNotification('Please check your email for the confirmation link.');
      setTimeout(() => {
        navigation.navigate('EmailSignIn', { redirect: undefined });
      }, 3000);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to sign up';
      if (message.toLowerCase().includes('confirmation link')) {
        setNotification(message);
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const clearMessages = () => {
    setError('');
    setNotification('');
  };

  return (
    <SafeAreaView style={commonStyles.safeArea}>
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <View style={[commonStyles.screenContainer, styles.formWrapper]}>
          <View style={commonStyles.form}>
            <TouchableOpacity
              onPress={() => navigation.goBack()}
              style={commonStyles.backButton}
            >
              <Ionicons name="arrow-back" size={24} color={theme.colors.textPrimary} />
              <Text style={commonStyles.backButtonText}>Back</Text>
            </TouchableOpacity>

            <Text style={commonStyles.screenTitle}>Create Account</Text>
            <Text style={commonStyles.screenSubtitle}>Sign up with your email</Text>

            {error ? (
              <View style={commonStyles.errorContainer}>
                <Text style={commonStyles.errorText}>{error}</Text>
              </View>
            ) : null}
            {notification ? (
              <View style={commonStyles.notificationContainer}>
                <Text style={commonStyles.notificationText}>{notification}</Text>
              </View>
            ) : null}

            <View style={commonStyles.formField}>
              <Text style={commonStyles.inputLabel}>Email</Text>
              <TextInput
                style={[commonStyles.input, error ? commonStyles.inputError : null]}
                placeholder="you@example.com"
                placeholderTextColor={theme.colors.textTertiary}
                value={email}
                onChangeText={(text) => {
                  setEmail(text);
                  clearMessages();
                }}
                autoCapitalize="none"
                keyboardType="email-address"
                autoComplete="email"
                editable={!loading}
              />
            </View>

            <View style={commonStyles.formField}>
              <Text style={commonStyles.inputLabel}>Password</Text>
              <TextInput
                style={[commonStyles.input, error ? commonStyles.inputError : null]}
                placeholder="••••••••"
                placeholderTextColor={theme.colors.textTertiary}
                value={password}
                onChangeText={(text) => {
                  setPassword(text);
                  clearMessages();
                }}
                secureTextEntry
                autoComplete="new-password"
                editable={!loading}
              />
            </View>

            <View style={commonStyles.formField}>
              <Text style={commonStyles.inputLabel}>Confirm Password</Text>
              <TextInput
                style={[commonStyles.input, error ? commonStyles.inputError : null]}
                placeholder="••••••••"
                placeholderTextColor={theme.colors.textTertiary}
                value={confirmPassword}
                onChangeText={(text) => {
                  setConfirmPassword(text);
                  clearMessages();
                }}
                secureTextEntry
                autoComplete="new-password"
                editable={!loading}
              />
            </View>

            <TouchableOpacity
              style={[commonStyles.primaryButton, loading && commonStyles.disabledButton]}
              onPress={handleSignUp}
              disabled={loading}
            >
              <Text style={commonStyles.primaryButtonText}>Sign Up</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={commonStyles.link}
              onPress={() => navigation.navigate('EmailSignIn', { redirect: undefined })}
              disabled={loading}
            >
              <Text style={commonStyles.linkText}>Already have an account? Sign In</Text>
            </TouchableOpacity>

            {loading && (
              <View style={commonStyles.loadingOverlay} pointerEvents="auto">
                <ActivityIndicator size={36} color={theme.colors.accent} />
              </View>
            )}
          </View>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  flex: {
    flex: 1,
  },
  formWrapper: {
    flex: 1,
    justifyContent: 'center',
  },
});
